import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { BookmarkAdd, BookmarkRemoveOutlined } from "@mui/icons-material";

import { ConfirmationModal } from "../../../components/ConfirmationModal";
import { CafsType } from "../../../consts/cafsType";
import { CafsTreeNodeType } from "../../../types/cafs-tree-node-type";
import { useCafsAssign } from "../api/useCafsAssign";
import { useCafsUnassign } from "../api/useCafsUnassign";
import { DashboardContext } from "../DashboardContext";

export type AssignCafsModalProps = {
  open: boolean;
  onClose: () => void;
  node: CafsTreeNodeType;
};

export function AssignCafsModal({ open, onClose, node }: AssignCafsModalProps) {
  const { selectedDataset } = useContext(DashboardContext);
  const { formatMessage } = useIntl();

  const { assignCafs, assignCafsPending } = useCafsAssign({
    cafs: node,
    onSuccess: onClose,
  });
  const { unassignCafs, unassignCafsPending } = useCafsUnassign({
    cafs: node,
    onSuccess: onClose,
  });

  const confidential =
    node.type === CafsType.Input
      ? node.confidentialSuffix
      : node.confidentialSpec;

  const cafsName = [node.name, confidential].filter(Boolean).join(" ");

  if (node.isAssigned) {
    return (
      <ConfirmationModal
        open={open}
        onConfirm={() => unassignCafs()}
        onCancel={onClose}
        variant="error"
        disabled={unassignCafsPending}
        icon={<BookmarkRemoveOutlined />}
        okText={formatMessage({
          id: "APP.REMOVE",
          defaultMessage: "Remove",
          description: "Remove general text",
        })}
        header={
          <FormattedMessage
            id="MODAL.UNASSIGN_CAFS_HEADER"
            defaultMessage="Remove CAFS from dataset"
            description="Remove CAFS from dataset modal header"
          />
        }
        text={
          <p>
            <FormattedMessage
              id="MODAL.UNASSIGN_CAFS_TEXT"
              defaultMessage="Do you want to remove {name} from dataset {dataset}?"
              values={{
                name: <span className="font-semibold">{cafsName}</span>,
                dataset: (
                  <span className="font-semibold">{selectedDataset?.Name}</span>
                ),
              }}
            />
          </p>
        }
      />
    );
  }

  return (
    <ConfirmationModal
      open={open}
      onConfirm={() => assignCafs()}
      onCancel={onClose}
      variant="primary"
      disabled={assignCafsPending}
      icon={<BookmarkAdd />}
      okText={formatMessage({
        id: "APP.ASSIGN",
        defaultMessage: "Assign",
        description: "Assign general text",
      })}
      header={
        <FormattedMessage
          id="MODAL.ASSIGN_CAFS_HEADER"
          defaultMessage="Assign CAFS to dataset"
          description="Assign CAFS to dataset modal header"
        />
      }
      text={
        <p>
          <FormattedMessage
            id="MODAL.ASSIGN_CAFS_TEXT"
            defaultMessage="Do you want to assign {name} to dataset {dataset}?"
            values={{
              name: <span className="font-semibold">{cafsName}</span>,
              dataset: (
                <span className="font-semibold">{selectedDataset?.Name}</span>
              ),
            }}
          />
        </p>
      }
    />
  );
}
